import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { filterDiscontinuedSexShopVariants } from 'constants/discontinuedSexShopVariants'
import nestApiService from 'services/api/nest-api-service'

export interface SingleItemsState {
  isLoading: boolean
  singleItems: SingleItem[]
  activeSingleItem: SingleItem | null
  isCustomizeSingleItemOpen: boolean
}

const initialState: SingleItemsState = {
  isLoading: false,
  singleItems: [],
  activeSingleItem: null,
  isCustomizeSingleItemOpen: false
}

export const singleItemsSlice = createSlice({
  name: 'single-items',
  initialState,
  reducers: {
    resetSingleItemsState: (state) => {
      state = initialState
      return state
    },
    onCustomizeSingleItemOpen: (state) => {
      state.isCustomizeSingleItemOpen = true
    },
    onCustomizeSingleItemClose: (state) => {
      state.isCustomizeSingleItemOpen = false
    },
    setActiveSingleItem: (state, action: PayloadAction<SingleItem | null>) => {
      state.activeSingleItem = action.payload
    },
    updateActiveSingleItem: (state, action: PayloadAction<Partial<SingleItem>>) => {
      if (state.activeSingleItem) {
        state.activeSingleItem = { ...state.activeSingleItem, ...action.payload }
      }
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getSingleItems.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getSingleItems.fulfilled, (state, action) => {
        state.isLoading = false
        state.singleItems = action.payload
      })
      .addCase(getSingleItems.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(getSingleItemsHomeV2.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getSingleItemsHomeV2.fulfilled, (state, action) => {
        state.isLoading = false
        state.singleItems = action.payload
      })
      .addCase(getSingleItemsHomeV2.rejected, (state) => {
        state.isLoading = false
      })
})

export const {
  resetSingleItemsState,
  onCustomizeSingleItemOpen,
  onCustomizeSingleItemClose,
  setActiveSingleItem,
  updateActiveSingleItem
} = singleItemsSlice.actions

export default singleItemsSlice.reducer

interface GetSingleItemsParams {
  patientId: number
}
export const mapSingleItemType = (item: SingleItem): SingleItem => {
  if (item.type) return item

  if (item.productId === 50) return { ...item, type: 'phyto' }

  if (
    item.productId &&
    [
      40, 94, 95, 96, 97, 1101, 1102, 1103, 1104, 1105, 1106, 1107, 1108, 1109, 1110, 1111, 1112, 1113, 1114, 1125
    ].includes(item.productId)
  ) {
    return { ...item, type: 'drugs' }
  }

  if ([494, 493].includes(item.variantId)) {
    return { ...item, type: 'sexShop' }
  }

  return item
}

export const filterDiscontinuedVariants = (items: SingleItem[]): SingleItem[] =>
  filterDiscontinuedSexShopVariants(items)

export const getSingleItems = createAsyncThunk(
  'get-single-items',
  async ({ patientId }: GetSingleItemsParams, { rejectWithValue }) => {
    try {
      const { data } = await nestApiService.apiInstance.get<SingleItem[]>('/single-item', {
        params: {
          patientId
        }
      })

      return filterDiscontinuedVariants(
        data.map((item, index) => ({
          ...mapSingleItemType(item),
          id: String(index)
        }))
      )
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getSingleItemsHomeV2 = createAsyncThunk(
  'get-single-items-home-v2',
  async ({ patientId }: GetSingleItemsParams, { rejectWithValue }) => {
    try {
      const { data } = await nestApiService.apiInstance.get<SingleItem[]>(`/single-item/singles?patientId=${patientId}`)

      return filterDiscontinuedVariants(data.map(mapSingleItemType))
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export type SingleItemType = 'drugs' | 'phyto' | 'psycho' | 'course' | 'sexShop'

export interface SingleItem {
  id: string
  name: string
  commercialName?: string
  learnMore?: string
  price: string | number
  singleItemPrice?: number
  variantId: number
  variantIds?: number[]
  // Promotion bundles: names of the doses inside, for the specialist tabs.
  specialtyNames?: string[]
  type: SingleItemType
  presentation?: string
  productPresentation?: string
  detailedTitle?: string
  description?: string
  credits?: number
  productId?: number
  psychologyDoctorId?: number
  label?: string
  image?: string
  isPhyto?: boolean
  priceMonthly?: number
  priceQuarterly?: number
  priceBimonthly?: number
  recommendedByDoctor?: boolean
  variants?: SingleItem[]
  pathology?: string
  is_glp1_daily?: boolean
  xis?: boolean
  // Xis offers are listed even without a usable prescription; false = show the
  // prescription notice and never add to the cart.
  eligible?: boolean
  penQuantity?: number
  promo?: boolean
  promotion?: {
    id: string
    name: string
    regularTotal: number
    promotionalTotal: number
    savings: number
    atomic: boolean
    eligibilityWarning: string
    // ECS-879 promos 2/3: per-dose variants from Nest ('combo') and the 2-box
    // offers built from them ('comboOffer'). maxBoxes = boxes of the dose the
    // patient's prescriptions cover.
    kind?: 'combo' | 'comboOffer'
    unitPrice?: number
    regularUnitPrice?: number
    maxBoxes?: number
    // Whether one box of each dose of the promotion is covered (ECS-879).
    mixedPairAllowed?: boolean
  }
}

export type SingleProductsGroupedByType = Record<SingleItemType, SingleItem[]>
export type SingleDrugsGroupedByType = Record<number, SingleItem[]>

export const customizableSingleItems = ['psycho', 'phyto', 'drugs']
